import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ColDef,
  ColGroupDef,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnVisibleEvent,
  GridApi,
  ICellRendererParams,
} from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'
import { cellRendererSelector as sharedCellRendererSelector } from '@/components/Grid/utils'

type ColumnDefItem<Row> = ColDef<Row> | ColGroupDef<Row>

type UseShowRowGroupOnFirstColumnParams<Row> = {
  api?: GridApi<Row> | null
  columnDefs: AgGridReactProps<Row>['columnDefs']
  enabled: boolean
}

const isColDef = <Row>(
  columnDef: ColumnDefItem<Row>,
): columnDef is ColDef<Row> => !('children' in columnDef)

const getColumnDefId = <Row>(columnDef: ColDef<Row>): string | null => {
  if (typeof columnDef.colId === 'string' && columnDef.colId.length > 0) {
    return columnDef.colId
  }
  if (typeof columnDef.field === 'string' && columnDef.field.length > 0) {
    return columnDef.field
  }
  return null
}

export const useShowRowGroupOnFirstColumn = <Row>({
  api,
  columnDefs,
  enabled,
}: UseShowRowGroupOnFirstColumnParams<Row>): AgGridReactProps<Row>['columnDefs'] => {
  const [groupHostColumnId, setGroupHostColumnId] = useState<string | null>(
    null,
  )
  const frameIdRef = useRef<number | null>(null)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const normalizedColumnDefs = Array.isArray(columnDefs)
    ? columnDefs
    : undefined
  const defaultGroupHostColumnId = useMemo(() => {
    if (!normalizedColumnDefs) return null
    return getFirstVisibleColumnDefId(normalizedColumnDefs)
  }, [normalizedColumnDefs])
  const effectiveGroupHostColumnId = useMemo(() => {
    if (!enabled) return null
    if (!normalizedColumnDefs) return null
    if (!groupHostColumnId) return defaultGroupHostColumnId

    const hostColumnStillExists = normalizedColumnDefs.some((columnDef) => {
      if (!isColDef<Row>(columnDef)) return false
      return (
        getColumnDefId(columnDef) === groupHostColumnId &&
        columnDef.hide !== true
      )
    })

    return hostColumnStillExists ? groupHostColumnId : defaultGroupHostColumnId
  }, [
    defaultGroupHostColumnId,
    enabled,
    groupHostColumnId,
    normalizedColumnDefs,
  ])
  const syncGroupHostFromApi = useCallback((targetApi: GridApi<Row>) => {
    const nextHostColumnId = resolveFirstDisplayedUserColumnId(targetApi)
    if (!nextHostColumnId) return

    setGroupHostColumnId((currentHostColumnId) =>
      currentHostColumnId === nextHostColumnId
        ? currentHostColumnId
        : nextHostColumnId,
    )
  }, [])
  const cancelScheduledSync = useCallback(() => {
    if (
      frameIdRef.current !== null &&
      typeof window !== 'undefined' &&
      typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(frameIdRef.current)
    }
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current)
    }
    frameIdRef.current = null
    timeoutIdRef.current = null
  }, [])
  const scheduleGroupHostSync = useCallback(
    (targetApi: GridApi<Row>) => {
      if (!enabled) return
      if (targetApi.isDestroyed?.()) return

      cancelScheduledSync()

      const runSync = () => {
        frameIdRef.current = null
        timeoutIdRef.current = null
        if (targetApi.isDestroyed?.()) return
        syncGroupHostFromApi(targetApi)
      }

      if (
        typeof window !== 'undefined' &&
        typeof window.requestAnimationFrame === 'function'
      ) {
        frameIdRef.current = window.requestAnimationFrame(runSync)
        return
      }

      timeoutIdRef.current = setTimeout(runSync, 0)
    },
    [cancelScheduledSync, enabled, syncGroupHostFromApi],
  )

  useEffect(() => {
    if (!enabled) return
    if (!api) return

    const onColumnMoved = (event: ColumnMovedEvent<Row>) => {
      if (event.finished === false) return
      scheduleGroupHostSync(api)
    }
    const onColumnVisible = (_event: ColumnVisibleEvent<Row>) => {
      scheduleGroupHostSync(api)
    }
    const onColumnPinned = (_event: ColumnPinnedEvent<Row>) => {
      scheduleGroupHostSync(api)
    }

    api.addEventListener('columnMoved', onColumnMoved)
    api.addEventListener('columnVisible', onColumnVisible)
    api.addEventListener('columnPinned', onColumnPinned)
    scheduleGroupHostSync(api)

    return () => {
      cancelScheduledSync()
      if (api.isDestroyed?.()) return
      api.removeEventListener('columnMoved', onColumnMoved)
      api.removeEventListener('columnVisible', onColumnVisible)
      api.removeEventListener('columnPinned', onColumnPinned)
    }
  }, [api, cancelScheduledSync, enabled, scheduleGroupHostSync])

  const patchedColumnDefs = useMemo(() => {
    if (!enabled) return columnDefs
    if (!normalizedColumnDefs) return columnDefs

    const nextColumnDefs = patchColumnDefsForGroupHost({
      columnDefs: normalizedColumnDefs,
      hostColumnId: effectiveGroupHostColumnId,
    })

    return nextColumnDefs ?? columnDefs
  }, [columnDefs, effectiveGroupHostColumnId, enabled, normalizedColumnDefs])

  return patchedColumnDefs
}

const resolveFirstDisplayedUserColumnId = <Row>(
  api: GridApi<Row>,
): string | null => {
  const displayedColumns = api.getAllDisplayedColumns()
  if (!Array.isArray(displayedColumns)) return null

  const firstDisplayedUserColumn = displayedColumns.find(
    (column) => !column.getColId().startsWith('ag-Grid-'),
  )
  return firstDisplayedUserColumn?.getColId() ?? null
}

const hasType = <Row>(
  columnDef: ColDef<Row>,
  expectedType: string,
): boolean => {
  const { type } = columnDef
  if (typeof type === 'string') return type === expectedType
  if (Array.isArray(type)) return type.includes(expectedType)
  return false
}

const supportsSharedGroupRenderer = <Row>(columnDef: ColDef<Row>): boolean => {
  return (
    columnDef.cellRenderer === 'agGroupCellRenderer' ||
    columnDef.cellRendererSelector === sharedCellRendererSelector ||
    hasType(columnDef, 'default')
  )
}

export const createGroupHostCellRendererSelector = <Row>(
  originalSelector: ColDef<Row>['cellRendererSelector'],
): ColDef<Row>['cellRendererSelector'] => {
  return (params) => {
    const sharedRenderer = sharedCellRendererSelector(
      params as ICellRendererParams,
    )
    if (sharedRenderer) return sharedRenderer
    return originalSelector?.(params)
  }
}

export const getFirstVisibleColumnDefId = <Row>(
  columnDefs: Array<ColumnDefItem<Row>>,
): string | null => {
  for (const columnDef of columnDefs) {
    if (!isColDef<Row>(columnDef)) continue
    if (columnDef.hide === true) continue
    const columnId = getColumnDefId(columnDef)
    if (columnId) return columnId
  }

  return null
}

export const patchColumnDefsForFirstGroupHost = <Row>({
  columnDefs,
}: {
  columnDefs: Array<ColumnDefItem<Row>>
}): AgGridReactProps<Row>['columnDefs'] | null => {
  return patchColumnDefsForGroupHost({
    columnDefs,
    hostColumnId: getFirstVisibleColumnDefId(columnDefs),
  })
}

export const patchColumnDefsForGroupHost = <Row>({
  columnDefs,
  hostColumnId,
}: {
  columnDefs: Array<ColumnDefItem<Row>>
  hostColumnId: string | null
}): AgGridReactProps<Row>['columnDefs'] | null => {
  if (!hostColumnId) return null

  let didChange = false

  const nextColumnDefs = columnDefs.map((columnDef) => {
    if (!isColDef<Row>(columnDef)) return columnDef

    const columnId = getColumnDefId(columnDef)
    const shouldHostRowGroup = columnId === hostColumnId
    const hasShowRowGroup = columnDef.showRowGroup !== undefined

    if (!shouldHostRowGroup && !hasShowRowGroup) {
      return columnDef
    }

    if (shouldHostRowGroup) {
      const needsGroupSelector = !supportsSharedGroupRenderer(columnDef)
      const nextShowRowGroup = columnDef.showRowGroup === true
      const nextCellRendererSelector = needsGroupSelector
        ? createGroupHostCellRendererSelector(columnDef.cellRendererSelector)
        : columnDef.cellRendererSelector

      if (!needsGroupSelector && nextShowRowGroup) {
        return columnDef
      }

      didChange = true

      return {
        ...columnDef,
        showRowGroup: true,
        ...(needsGroupSelector
          ? { cellRendererSelector: nextCellRendererSelector }
          : {}),
      }
    }

    didChange = true
    const { showRowGroup: _showRowGroup, ...restColumnDef } = columnDef
    return restColumnDef
  })

  return didChange ? nextColumnDefs : null
}
